import { useEffect, useRef, useState } from 'react';
import { getAdminGallery, uploadGalleryImages, deleteGalleryImage } from '../utils/api';
import { closeModalWithAnimation } from '../utils/modalHelpers';
import AdminPageSkeleton from '../components/AdminPageSkeleton';
import ConfirmModal from '../components/ConfirmModal';
import Error500 from './errors/Error500';
import './AdminPages.css';

const AdminGalleryPage = () => {
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState(false);
  const [status, setStatus] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOrientation, setPreviewOrientation] = useState(null);
  const previewOverlayRef = useRef(null);

  useEffect(() => {
    loadGallery();
  }, []);

  const loadGallery = async () => {
    try {
      setLoading(true);
      const response = await getAdminGallery();
      const data = response.data.data || response.data || [];
      setImages(data);
      setServerError(false);
    } catch (err) {
      console.error('Failed to load gallery:', err);
      if (!err.response || err.response.status >= 500 || err.code === 'ERR_NETWORK') {
        setServerError(true);
        return;
      }
      setStatus({ type: 'error', message: 'Nepodarilo sa načítať galériu.' });
    } finally {
      setLoading(false);
    }
  };

  const formatSize = (size = 0) => {
    if (!size) return '—';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      const date = new Date(value);
      const datePart = date.toLocaleDateString('sk-SK', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const timePart = date.toLocaleTimeString('sk-SK', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return `${datePart} • ${timePart}`;
    } catch {
      return '—';
    }
  };

  const handleFilesChange = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
    setStatus(null);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      return;
    }

    try {
      setUploading(true);
      setStatus(null);
      const batchSize = 5;
      const batches = [];
      for (let i = 0; i < selectedFiles.length; i += batchSize) {
        batches.push(selectedFiles.slice(i, i + batchSize));
      }

      const allErrors = [];
      let uploadedCount = 0;

      for (let i = 0; i < batches.length; i++) {
        setUploadProgress({ current: i + 1, total: batches.length });
        const formData = new FormData();
        batches[i].forEach(file => formData.append('images[]', file));

        try {
          const response = await uploadGalleryImages(formData);
          const errors = response?.data?.data?.errors || [];
          const uploaded = response?.data?.data?.uploaded || [];
          uploadedCount += uploaded.length;
          if (errors.length > 0) {
            allErrors.push(...errors);
          }
        } catch (err) {
          allErrors.push(err.response?.data?.message || 'Nepodarilo sa nahrať fotky.');
        }
      }

      if (allErrors.length > 0) {
        setStatus({
          type: 'warning',
          message: `Niektoré fotky sa nepodarilo nahrať: ${allErrors.join(' • ')}`
        });
      } else {
        setStatus(null);
      }

      if (uploadedCount > 0) {
        await loadGallery();
      }

      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Failed to upload gallery images:', err);
      setStatus({ type: 'error', message: err.response?.data?.message || 'Nepodarilo sa nahrať fotky.' });
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleDeleteClick = (image) => {
    setImageToDelete(image);
    setShowConfirmModal(true);
  };

  const handleCloseConfirmModal = () => {
    closeModalWithAnimation(() => {
      setShowConfirmModal(false);
      setImageToDelete(null);
    });
  };

  const confirmDelete = async () => {
    if (!imageToDelete) return;
    const filename = imageToDelete.filename;
    handleCloseConfirmModal();

    try {
      await deleteGalleryImage(filename);
      setImages(prev => prev.filter(image => image.filename !== filename));
    } catch (err) {
      console.error('Failed to delete image:', err);
      setStatus({ type: 'error', message: err.response?.data?.message || 'Nepodarilo sa vymazať fotku.' });
    }
  };

  const handlePreviewOpen = (image) => {
    setPreviewOrientation(null);
    setPreviewImage(image);
  };

  const handlePreviewClose = () => {
    closeModalWithAnimation(() => setPreviewImage(null), previewOverlayRef.current);
  };
  const handlePreviewLoaded = (event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (!naturalWidth || !naturalHeight) return;
    setPreviewOrientation(naturalWidth >= naturalHeight ? 'landscape' : 'portrait');
  };

  if (loading) {
    return (
      <AdminPageSkeleton
        title="Galéria"
        subtitle="Spravujte fotky zobrazované v sekcii O nás."
        variant="gallery"
      />
    );
  }

  if (serverError) {
    return <Error500 />;
  }

  return (
    <div className="admin-page admin-gallery-page">
      <div className="page-header header-actions">
        <div>
          <h1>Galéria</h1>
          <p>Spravujte fotky zobrazované v sekcii O nás.</p>
        </div>
        <div className="gallery-header-right">
          <span className="gallery-count-label">
            {images.length === 1 ? '1 fotka v galérii' : images.length < 5 ? `${images.length} fotky v galérii` : `${images.length} fotiek v galérii`}
          </span>
          <button
            type="button"
            className="btn btn-primary btn-lg"
            onClick={handleUpload}
            disabled={uploading || selectedFiles.length === 0}
          >
            {uploading
              ? uploadProgress
                ? `Nahrávam ${uploadProgress.current}/${uploadProgress.total}`
                : 'Nahrávam...'
              : 'Nahrať fotky'}
          </button>
        </div>
      </div>

      {status?.type === 'error' && (
        <div className={`alert alert-${status.type} mb-3`}>
          {status.message}
        </div>
      )}

      <div className="table-container">
        <div className="admin-gallery-upload">
          <div>
            <h3>Pridať fotky</h3>
            <p>Podporované formáty: JPG, PNG, WEBP. Max 8 MB.</p>
          </div>
          <div className="admin-gallery-actions">
            <div className="admin-file-input">
              <input
                ref={fileInputRef}
                id="gallery-upload"
                type="file"
                className="admin-file-hidden"
                multiple
                accept=".jpg,.jpeg,.png,.webp"
                onChange={handleFilesChange}
              />
              <label className="admin-file-label" htmlFor="gallery-upload">
                Vybrať fotky na upload
              </label>
              <span className="admin-file-meta">
                {selectedFiles.length > 0
                  ? `Vybrané: ${selectedFiles.length}`
                  : 'Žiadne nahraté súbory'}
              </span>
            </div>
            {/* <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0}
            >
              {uploading ? 'Nahrávam...' : 'Nahrať fotky'}
            </button> */}
          </div>
        </div>
      </div>

      <div className="table-container">
        {images.length === 0 ? (
          <div className="empty-state">
            <svg className="empty-state-icon" width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden="true">
              <rect x="9" y="12" width="38" height="32" rx="6" stroke="currentColor" strokeWidth="2"/>
              <path d="M17 34l8-8 6 6 8-8 8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="20" cy="21" r="3" fill="currentColor"/>
            </svg>
            Zatiaľ neboli nahrané žiadne fotky.
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Náhľad</th>
                <th>Názov súboru</th>
                <th>Veľkosť</th>
                <th>Dátum nahratia</th>
                <th>Akcie</th>
              </tr>
            </thead>
            <tbody>
              {images.map((image) => (
                <tr key={image.filename}>
                  <td className="td-center">
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="btn-icon btn-info"
                        onClick={() => handlePreviewOpen(image)}
                        title="Náhľad"
                        aria-label={`Náhľad ${image.filename}`}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/>
                          <circle cx="12" cy="12" r="3.5"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td>{image.filename}</td>
                  <td>{formatSize(image.size)}</td>
                  <td>{formatDate(image.updated_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleDeleteClick(image)}
                        title="Vymazať"
                      >
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
                          <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3h11V2h-11v1Z"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmModal
        show={showConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={confirmDelete}
        title="Vymazať fotku"
        message={`Naozaj chcete vymazať fotku "${imageToDelete?.filename}"?`}
        confirmText="Vymazať"
        confirmButtonClass="btn-danger"
      />

      {previewImage && (
        <div className="modal-overlay" ref={previewOverlayRef} onClick={handlePreviewClose}>
          <div
            className={`modal-content modal-preview${previewOrientation ? ` is-${previewOrientation}` : ''}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Náhľad fotky</h2>
              <button className="modal-close" onClick={handlePreviewClose}>×</button>
            </div>
            <div className="modal-body">
              <img
                className="gallery-preview-image"
                src={previewImage.url}
                alt={previewImage.filename}
                onLoad={handlePreviewLoaded}
              />
              <p className="gallery-preview-name">{previewImage.filename}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGalleryPage;
